/**
 * Client-side mirror of supabase/functions/_shared/personality-compiler.ts
 *
 * Used by the Turn Inspector (MS-03) to render the **exact same** compiled
 * personality block that the edge functions would attach to the LLM system
 * prompt — without touching the edge code.
 *
 * Keep this file in sync if the server-side compiler changes.
 */

export type ConversationType = "group" | "direct";

export interface AgentPersonality {
  response_frequency: number;
  response_length: "curta" | "média" | "longa" | "adaptativa";
  formality: number;
  proactivity: number;
  objectivity: number;
  social_posture: "neutra" | "acolhedora" | "firme" | "provocadora" | "mediadora";
  caution_level: number;
  question_vs_assert: number;
  mediation_style: "passiva" | "ativa" | "arbitral" | "silenciosa";
  group_behavior?: Record<string, unknown> | null;
  direct_behavior?: Record<string, unknown> | null;
  temperature_override?: number | null;
}

const LEN_HINT: Record<AgentPersonality["response_length"], string> = {
  curta: "≤2 frases. Vá direto ao ponto.",
  "média": "1 parágrafo curto (3-5 frases).",
  longa: "Pode usar até 2 parágrafos quando o conteúdo justificar.",
  adaptativa: "Ajuste o tamanho à complexidade da pergunta — não infle por inflar.",
};

function band(n: number, low: string, mid: string, high: string): string {
  if (n <= 33) return low;
  if (n <= 66) return mid;
  return high;
}

export function compilePersonalityPrompt(
  p: AgentPersonality,
  convType: ConversationType,
): string {
  const formalityTone = band(
    p.formality,
    "Linguagem informal, próxima, com abreviações ocasionais. Evite tratamentos cerimoniosos.",
    "Linguagem cordial e profissional, sem rigidez nem gírias.",
    "Linguagem formal, precisa, sem informalidades.",
  );
  const objectivityTone = band(
    p.objectivity,
    "Pode ser conversacional, dar contexto, fazer rodeios curtos quando ajudar.",
    "Equilibre objetividade com leveza.",
    "Seja extremamente objetivo. Sem preâmbulos. Sem encerramentos vazios.",
  );
  const proactivityTone = band(
    p.proactivity,
    "Só intervenha quando perguntada ou claramente solicitada. Não sugira de proativamente.",
    "Pode oferecer sugestões pontuais quando relevante, mas sem forçar.",
    "Antecipe necessidades. Sugira próximos passos. Identifique loops abertos.",
  );
  const cautionTone = band(
    p.caution_level,
    "Aceite premissas razoáveis e responda. Não peça confirmação para tudo.",
    "Em caso de ambiguidade real, peça uma única clarificação curta.",
    "Quando houver qualquer ambiguidade ou risco de erro factual, pergunte antes de responder. Prefira admitir 'não sei' a inventar.",
  );
  const questionVsAssertTone = band(
    p.question_vs_assert,
    "Prefira afirmações claras. Use perguntas só quando estritamente necessário.",
    "Misture afirmações e perguntas conforme o contexto.",
    "Prefira responder perguntando — devolva o turno ao usuário com perguntas socráticas.",
  );

  const postureLine: Record<AgentPersonality["social_posture"], string> = {
    neutra: "Postura neutra: sem afeto exagerado, sem frieza.",
    acolhedora: "Postura acolhedora: valide o que o usuário trouxe antes de responder o conteúdo.",
    firme: "Postura firme: defenda posições com clareza, sem agressividade.",
    provocadora: "Postura provocadora: faça perguntas que desafiem suposições.",
    mediadora: "Postura mediadora: escute todos os lados antes de tomar posição.",
  };
  const mediationLine: Record<AgentPersonality["mediation_style"], string> = {
    passiva: "Em conflito no grupo: observe, não tome partido salvo se solicitada.",
    ativa: "Em conflito no grupo: intervenha resumindo posições e propondo síntese.",
    arbitral: "Em conflito no grupo: arbitre com base em fatos e regras explícitas.",
    silenciosa: "Em conflito no grupo: fique silenciosa salvo solicitação direta.",
  };

  const freqLine =
    p.response_frequency <= 20
      ? "Frequência de resposta MUITO BAIXA: só responda se mencionada (@) ou em pergunta direta."
      : p.response_frequency <= 50
      ? "Frequência de resposta BAIXA: priorize silêncio quando não houver valor claro em responder."
      : p.response_frequency <= 80
      ? "Frequência de resposta MÉDIA: responda quando houver utilidade clara."
      : "Frequência de resposta ALTA: contribua sempre que tiver valor a agregar, sem ser invasiva.";

  const ctxLine =
    convType === "group"
      ? "Contexto: GRUPO. Considere múltiplos participantes, evite roubar a cena, respeite turnos."
      : "Contexto: 1:1. Conversa privada — pode ser mais direta e personal.";

  const overrides = convType === "group" ? p.group_behavior : p.direct_behavior;
  const overrideLines = overrides && Object.keys(overrides).length
    ? `Overrides para este contexto: ${JSON.stringify(overrides)}`
    : "";

  return [
    "[PERSONALIDADE COMPILADA]",
    ctxLine,
    `• Tamanho: ${LEN_HINT[p.response_length]}`,
    `• Formalidade: ${formalityTone}`,
    `• Objetividade: ${objectivityTone}`,
    `• Proatividade: ${proactivityTone}`,
    `• Cautela: ${cautionTone}`,
    `• Estilo: ${questionVsAssertTone}`,
    `• ${postureLine[p.social_posture]}`,
    `• ${mediationLine[p.mediation_style]}`,
    `• ${freqLine}`,
    overrideLines,
  ].filter(Boolean).join("\n");
}
